import { computed, ref } from 'vue';

type ViewerPhase = 'closed' | 'opening' | 'open' | 'closing';

interface ViewerSourceRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

const viewerPhase = ref<ViewerPhase>('closed');
const imageSrc = ref<string | null>(null);
const imageAltText = ref<string | null>(null);
const imageTitle = ref<string | null>(null);
const imageSourceRect = ref<ViewerSourceRect | null>(null);
const imageViewIsOpen = computed(() => viewerPhase.value !== 'closed');

const bodyOverflowCache = ref('auto');

const setBodyOverflow = (value: string) => {
  document.body.style.overflow = value;
};

export function useImageViewer() {
  const openImageMenu = (src: string, title: string, sourceElement?: EventTarget | null) => {
    if (viewerPhase.value !== 'closed') return;

    imageSourceRect.value = sourceElement instanceof HTMLElement ? sourceElement.getBoundingClientRect() : null;
    imageSrc.value = src;
    imageAltText.value = title;
    imageTitle.value = title;
    bodyOverflowCache.value = document.body.style.overflow;
    setBodyOverflow('hidden');
    viewerPhase.value = 'opening';
  };

  const closeImageMenu = () => {
    if (viewerPhase.value !== 'open' && viewerPhase.value !== 'opening') return;
    viewerPhase.value = 'closing';
  };

  const markImageMenuOpen = () => {
    if (viewerPhase.value === 'opening') {
      viewerPhase.value = 'open';
    }
  };

  const finalizeImageMenuClose = () => {
    if (viewerPhase.value !== 'closing') return;
    viewerPhase.value = 'closed';
    imageSrc.value = null;
    imageAltText.value = null;
    imageTitle.value = null;
    imageSourceRect.value = null;
    setBodyOverflow(bodyOverflowCache.value || 'auto');
  };

  return {
    openImageMenu,
    closeImageMenu,
    markImageMenuOpen,
    finalizeImageMenuClose,

    imageViewIsOpen,
    imageSrc,
    imageAltText,
    imageTitle,
    imageSourceRect,
    viewerPhase,
  }
}